import { Action, authenticate, CognitoState, mkAttrList } from 'react-cognito';
import config from '../config';
import {
  EMPTY_OBJECT,
  MODAL,
  ONBOARDING_STEP,
  SET_MODAL,
  SET_PROFILE_IMAGE_SRC
} from '../constants';
import rollbar from '../lib/rollbar';
import { fetch } from './api';
import { getLicenses } from './clips';
import { buildAction } from './helpers';
import { showModal } from './modal';
import { getActiveSubscription } from './plan';
import { setRuntimeVariable } from './runtime';

export function showSignup() {
  return function(dispatch) {
    dispatch(setRuntimeVariable('onboardingStep', ONBOARDING_STEP.REGISTER));
    dispatch(setRuntimeVariable('registrationError', undefined));
    dispatch(Action.beginPasswordResetFlow(undefined, '')); // HACK: Reset any react-cognito error.
    dispatch(buildAction(SET_MODAL, MODAL.ONBOARDING));
    dispatch(showModal());
  };
}

export function showLogin(modalProps) {
  return function(dispatch) {
    dispatch(setRuntimeVariable('onboardingStep', ONBOARDING_STEP.LOGIN));
    dispatch(setRuntimeVariable('loginError', undefined));
    dispatch(Action.beginPasswordResetFlow(undefined, '')); // HACK: Reset any react-cognito error.
    dispatch(buildAction(SET_MODAL, MODAL.ONBOARDING));
    dispatch(showModal(modalProps));
  };
}

export function signup(email, password) {
  return function(dispatch, getState) {
    const { userPool, config } = getState().cognito;
    const lowerEmail = email.toLowerCase();

    dispatch(setRuntimeVariable('registrationError', undefined));

    return new Promise(function(resolve, reject) {
      userPool.signUp(
        lowerEmail,
        password,
        mkAttrList({ email: lowerEmail }),
        null,
        function(err, result) {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        },
      );
    })
      .then(result => {
        if (result.userConfirmed === false) {
          dispatch(Action.confirmationRequired(result.user, lowerEmail));
        } else {
          return authenticate(lowerEmail, password, userPool, config, dispatch);
        }
      })
      .catch(err => {
        dispatch(
          setRuntimeVariable('registrationError', parseCognitoErrors(err)),
        );
        return Promise.reject(err);
      })
      .then(() =>
        rollbar.configure({
          payload: {
            person: {
              id: lowerEmail,
              email: lowerEmail,
            },
          },
        }),
      );
  };
}

export function logout() {
  return function(dispatch, getState) {
    const { user } = getState().cognito;
    if (user) {
      user.signOut();
    }

    rollbar.configure({ payload: { person: EMPTY_OBJECT } });

    return dispatch(Action.logout());
    // TODO: Purge user data from state. Reload page?
  };
}

export function changeEmail(email) {
  const lowerEmail = email.toLowerCase();

  return async function(dispatch, getState) {
    const cognito = getState().cognito;

    return new Promise((resolve, reject) =>
      cognito.user.updateAttributes(
        [{ Name: 'preferred_username', Value: lowerEmail }],
        (err, result) => {
          if (err) {
            console.log('got an error changing the username', err);
            reject(err.message);
          } else {
            console.log('preferred_username updated!');
            resolve(result);
          }
        },
      ),
    )
      .then(() =>
        rollbar.configure({
          payload: {
            person: {
              id: cognito.attributes.email,
              email: lowerEmail,
            },
          },
        }),
      );
  };
}

export function changePassword(oldPassword, newPassword) {
  return async function(dispatch, getState) {
    const user = getState().cognito.user;

    return new Promise((resolve, reject) =>
      user.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err.message);
        } else {
          resolve(result);
        }
      }),
    );
  };
}

export function changeName(givenName, familyName) {
  return async function(dispatch, getState) {
    const cognito = getState().cognito;

    return new Promise((resolve, reject) =>
      cognito.user.updateAttributes(
        [
          { Name: 'given_name', Value: givenName },
          { Name: 'family_name', Value: familyName },
        ],
        (err, result) => {
          if (err) {
            reject(err.message);
          } else {
            resolve(result);
          }
        },
      ),
    )
      .then(() =>
        rollbar.configure({
          payload: {
            person: {
              id: cognito.attributes.email,
              username: `${givenName} ${familyName}`,
              email:
                cognito.user.preferred_username || cognito.attributes.email,
            },
          },
        }),
      );
  };
}

function setProfileImageSrc(src) {
  return buildAction(SET_PROFILE_IMAGE_SRC, src);
}

function getPofileImageKey(sub) {
  if (sub) {
    return `customer-uploads/${sub}/profileimage`;
  }
}

function getProfileImageSrc(sub, x) {
  const key = getPofileImageKey(sub);
  if (!key) {
    return;
  }

  let src = `https://s3.amazonaws.com/${config.api.uploadBucket}/${key}`;
  if (x) {
    src = `${src}?x=${x}`;
  }
  return src;
}

export function uploadProfileImage(file) {
  return function(dispatch, getState) {
    const cognito = getState().cognito;

    AWS.config.credentials = cognito.creds;
    const s3 = new AWS.S3();
    const params = {
      Bucket: config.api.uploadBucket,
      Key: getPofileImageKey(cognito.creds.identityId),
      ContentType: file.type,
      Body: file,
      ACL: 'public-read',
    };

    return s3
      .putObject(params)
      .promise()
      .then(() =>
        dispatch(
          fetch(getProfileImageSrc(cognito.creds.identityId), {
            mode: 'no-cors',
            cache: 'reload',
          }),
        ),
      )
      .then(() =>
        dispatch(
          setProfileImageSrc(
            getProfileImageSrc(cognito.creds.identityId, Date.now()),
          ),
        ),
      );
  };
}

export function onLogin() {
  return function(dispatch, getState) {
    const cognito = getState().cognito;

    let username = [
      cognito.attributes.given_name,
      cognito.attributes.family_name,
    ].join(' ');
    if (username === ' ') {
      username = undefined;
    }

    rollbar.configure({
      payload: {
        person: {
          id: cognito.attributes.email,
          username: username,
          email: cognito.user.preferred_username || cognito.attributes.email,
        },
      },
    });
    dispatch(setProfileImageSrc(getProfileImageSrc(cognito.creds.identityId)));
    dispatch(getActiveSubscription());
    return dispatch(getLicenses());
    // TODO: Load profile.
  };
}

export function setupOnLogin(store) {
  let currentLoginState;

  const handleChange = function() {
    const nextState = store.getState();
    if (currentLoginState !== nextState.cognito.state) {
      currentLoginState = nextState.cognito.state;
      // We know the state just changed from X -> Y so if Y is LOGGED_IN we know X is not LOGGED_IN.
      if (currentLoginState === CognitoState.LOGGED_IN) {
        store.dispatch(onLogin());
      }
    }
  };

  const unsub = store.subscribe(handleChange);
  handleChange();
  return unsub;
}

function parseCognitoErrors(error) {
  let message = error;
  if (error instanceof Error) {
    message = error.message;
  }
  message = String(message);

  if (message === 'Incorrect username or password.') {
    return 'Incorrect email address or password.';
  }

  if (message.includes("Value at 'password' failed to satisfy constraint")) {
    return 'Password must be at least 8 characters long.';
  }

  if (message.includes('account with the given email already exists.')) {
    return 'That email address is already registered.';
  }

  return message;
}

export function getStep(state) {
  // Based on the desired step in the state plus the logged in state of the user return the step they
  // need to complete next to move to the desired step.
  const desiredStep = state.runtime.onboardingStep;
  const userState = state.cognito.state;
  const user = state.cognito.user;

  switch (desiredStep) {
    case ONBOARDING_STEP.REGISTER:
    case ONBOARDING_STEP.LOGIN:
      return desiredStep;
  }

  // Must be 'logged in' beyond here.
  if (!user || userState === CognitoState.LOGGED_OUT) {
    // Places you can go while not logged in.
    switch (desiredStep) {
      case ONBOARDING_STEP.REGISTER:
      case ONBOARDING_STEP.LOGIN:
      case ONBOARDING_STEP.FORGOT_PASSWORD:
      case ONBOARDING_STEP.RESET_PASSWORD:
        return desiredStep;
    }
    return ONBOARDING_STEP.LOGIN;
  }

  // If the user has to make changes to their account capture them first.
  switch (userState) {
    case CognitoState.NEW_PASSWORD_REQUIRED:
      return ONBOARDING_STEP.NEW_PASSWORD_REQUIRED;
    case CognitoState.EMAIL_VERIFICATION_REQUIRED:
    case CognitoState.CONFIRMATION_REQUIRED:
      return ONBOARDING_STEP.CONFIRM;
  }

  // This shouldn't happen but lets catch it.
  if (userState !== CognitoState.LOGGED_IN) {
    console.error('Failed to get a logged in user.', userState, user);
    return ONBOARDING_STEP.LOGIN;
  }

  if (state.plan.activePlan.name === undefined) {
    if (desiredStep === ONBOARDING_STEP.COMPLETE) {
      return ONBOARDING_STEP.PLAN;
    }
  }

  return desiredStep;
}



// WEBPACK FOOTER //
// ./src/actions/user.js